import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Gauge } from "lucide-react";

type TonnageSelectStepProps = {
  title: string;
  options: { value: string; label: string }[];
  value?: string;
  description?: string;
  placeholder?: string;
  icon?: ReactNode;
  onChange: (value: string) => void;
};

function normalizeLabel(value: string) {
  return value.replace(/^"+|"+$/g, "").trim();
}

export function TonnageSelectStep({
  title,
  options,
  value,
  description,
  placeholder = "Choose tonnage",
  icon,
  onChange,
}: TonnageSelectStepProps) {
  const [open, setOpen] = useState(false);
  const [openUpwards, setOpenUpwards] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const cleanOptions = useMemo(
    () =>
      options.map((opt) => ({
        ...opt,
        label: normalizeLabel(opt.label),
      })),
    [options],
  );

  const selected = cleanOptions.find((opt) => opt.value === value);

  useEffect(() => {
    function updateDropdownDirection() {
      if (!triggerRef.current) return;

      const rect = triggerRef.current.getBoundingClientRect();
      const estimatedMenuHeight = Math.min(cleanOptions.length * 48, 280) + 12;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      setOpenUpwards(spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow);
    }

    if (!open) return;

    updateDropdownDirection();
    window.addEventListener("resize", updateDropdownDirection);
    window.addEventListener("scroll", updateDropdownDirection, true);

    return () => {
      window.removeEventListener("resize", updateDropdownDirection);
      window.removeEventListener("scroll", updateDropdownDirection, true);
    };
  }, [open, cleanOptions.length]);

  useEffect(() => {
    function handleOutsideClick(event: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  return (
    <section className="space-y-4">
      <header className="space-y-1">
        <h2 className="text-xl font-bold text-[var(--text-primary)]">{title}</h2>
        {description && <p className="text-sm text-[var(--text-muted)]">{description}</p>}
      </header>

      <div ref={wrapperRef} className={`relative ${open ? "z-50" : "z-0"}`}>
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((s) => !s)}
          className="
            flex w-full items-center justify-between rounded-2xl border border-[var(--line)]
            bg-white px-4 py-3 text-left shadow-sm transition hover:border-gray-300
            focus:outline-none focus:ring-2 focus:ring-[var(--brand)]
          "
        >
          <span className="flex items-center gap-3">
            <span
              className={`
                flex h-9 w-9 items-center justify-center rounded-xl
                ${selected ? "bg-[var(--brand-soft)] text-[var(--brand)]" : "bg-gray-100 text-gray-500"}
              `}
            >
              {icon ?? <Gauge size={18} />}
            </span>
            <span className="font-semibold text-[var(--text-primary)]">
              {selected ? selected.label : placeholder}
            </span>
          </span>
          <ChevronDown
            size={18}
            className={`text-[var(--text-muted)] transition ${open ? "rotate-180" : ""}`}
          />
        </button>

        {open && (
          <div
            className={`
              absolute z-50 w-full overflow-hidden rounded-2xl border border-[var(--line)]
              bg-white shadow-xl
              ${openUpwards ? "bottom-full mb-2" : "top-full mt-2"}
            `}
          >
            <div className="max-h-[280px] overflow-auto">
              {cleanOptions.map((opt) => {
              const isSelected = opt.value === value;

              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className={`
                    flex w-full items-center justify-between px-4 py-3 text-left
                    transition hover:bg-gray-50
                    ${isSelected ? "bg-[var(--brand-soft)]" : ""}
                  `}
                >
                  <span className="font-medium text-[var(--text-primary)]">{opt.label}</span>
                  {isSelected && <Check size={16} className="text-[var(--brand)]" />}
                </button>
              );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
